import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { ScanLine, CheckCircle2, XCircle, AlertTriangle, Ticket } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/audit";
import { useLanguage } from "@/hooks/useLanguage";
import {
  lookupVoucher,
  checkVoucher,
  redeemVoucher,
  rejectVoucher,
  normalizeCode,
  type ValidatorContext,
} from "@/lib/rewards/voucherValidation";
import type { Voucher } from "@/lib/rewards/voucherEngine";

export const Route = createFileRoute("/validar-voucher")({
  head: () => ({ meta: [{ title: "Validar Voucher · VerseSport™" }] }),
  component: ValidatePortalPage,
});

function ValidatePortalPage() {
  const { t } = useLanguage();
  const [ctx, setCtx] = useState<ValidatorContext | null>(null);
  const [authzLoading, setAuthzLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [voucher, setVoucher] = useState<Voucher | null>(null);
  const [check, setCheck] = useState<{ ok: boolean; reason?: string } | null>(null);
  const [scannerOn, setScannerOn] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);

  useEffect(() => {
    void trackEvent("reward_validation_portal_opened", {});
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setAuthError(t("vvp_need_login")); setAuthzLoading(false); return; }
      const { data: roles } = await supabase.from("user_roles").select("role").eq("user_id", user.id);
      const roleSet = new Set((roles ?? []).map((r) => r.role));
      const isAdmin = roleSet.has("admin");
      const isValidator = roleSet.has("partner_validator");
      if (!isAdmin && !isValidator) { setAuthError(t("vvp_no_access")); setAuthzLoading(false); return; }
      type PartnerBinding = {
        partner_id: string;
        reward_partners: { id: string; name: string; country: string | null; city: string | null } | null;
      };
      let partner: PartnerBinding | null = null;
      if (isValidator) {
        const { data: bind } = await supabase
          .from("partner_validators")
          .select("partner_id, reward_partners(id,name,country,city)")
          .eq("user_id", user.id)
          .eq("is_active", true)
          .maybeSingle();
        partner = (bind as unknown as PartnerBinding | null) ?? null;
      }
      setCtx({
        userId: user.id,
        role: isAdmin ? "admin" : "partner_validator",
        partnerId: partner?.partner_id ?? null,
        partnerName: partner?.reward_partners?.name ?? null,
        partnerCountry: partner?.reward_partners?.country ?? null,
        partnerCity: partner?.reward_partners?.city ?? null,
      });
      setAuthzLoading(false);
    })();
    return () => { void scannerRef.current?.stop().catch(() => {}); };
  }, [t]);

  const startScanner = async () => {
    setScannerOn(true);
    setTimeout(async () => {
      try {
        const el = "vvp-scanner";
        scannerRef.current = new Html5Qrcode(el);
        await scannerRef.current.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: 220 },
          async (decoded) => {
            const m = decoded.match(/SS2026:([A-Z0-9-]+):/);
            const c = m ? m[1] : decoded;
            await scannerRef.current?.stop();
            setScannerOn(false);
            setCode(c);
            await doLookup(c);
          },
          () => {},
        );
      } catch (e) {
        toast.error(String(e));
        setScannerOn(false);
      }
    }, 50);
  };

  const stopScanner = async () => { await scannerRef.current?.stop().catch(() => {}); setScannerOn(false); };

  const doLookup = async (raw: string) => {
    const v = await lookupVoucher(raw);
    setVoucher(v);
    setCheck(checkVoucher(v, ctx ?? undefined));
  };

  const onRedeem = async () => {
    if (!voucher || !ctx) return;
    const r = await redeemVoucher(voucher, ctx);
    if (r.ok) {
      toast.success(t("vvp_redeemed"));
      setVoucher(r.voucher);
      setCheck({ ok: false, reason: "already_redeemed" });
    } else {
      toast.error(t(`vvp_err_${r.reason}`) || r.reason);
    }
  };

  const onReject = async () => {
    if (!voucher || !ctx) return;
    await rejectVoucher(voucher, ctx);
    toast.success(t("vvp_rejected"));
    setVoucher(null); setCheck(null); setCode("");
  };

  if (authzLoading) return <div className="p-8 text-center text-sm text-muted-foreground">{t("loading")}</div>;
  if (authError) {
    return (
      <div className="mx-auto max-w-md p-6 text-center">
        <AlertTriangle className="mx-auto h-10 w-10 text-[oklch(0.78_0.2_50)]" />
        <h1 className="mt-3 text-lg font-bold">{t("vvp_title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{authError}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md space-y-4 p-4">
      <header className="flex items-center gap-2">
        <Ticket className="h-5 w-5 text-[oklch(0.85_0.18_85)]" />
        <h1 className="text-base font-black">{t("vvp_title")}</h1>
      </header>
      {ctx?.partnerName && <div className="text-[11px] text-muted-foreground">{t("vvp_acting_as")}: <b>{ctx.partnerName}</b></div>}

      <div className="rounded-2xl border border-border/60 p-3 space-y-2">
        <button
          onClick={scannerOn ? stopScanner : startScanner}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground"
        >
          <ScanLine className="h-4 w-4" /> {scannerOn ? t("vvp_stop_scan") : t("vvp_start_scan")}
        </button>
        {scannerOn && <div id="vvp-scanner" className="overflow-hidden rounded-xl bg-black" />}
        <div className="text-center text-[10px] text-muted-foreground">{t("vvp_or")}</div>
        <div className="flex gap-2">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="XXXX-XXXX"
            className="flex-1 rounded-lg border border-border/60 bg-background px-3 py-2 font-mono text-sm uppercase tracking-widest"
          />
          <button onClick={() => doLookup(normalizeCode(code))} className="rounded-lg bg-accent px-4 text-xs font-bold">
            {t("vvp_lookup")}
          </button>
        </div>
      </div>

      {voucher && (
        <div className="rounded-2xl border border-border/60 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-black">{(voucher.metadata as { reward_name?: string } | null)?.reward_name ?? voucher.code}</div>
              <div className="text-[10px] text-muted-foreground">{voucher.partner?.name ?? "—"}</div>
            </div>
            <StatusBadge status={voucher.status} />
          </div>
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <Info label={t("vvp_code")} value={voucher.code} mono />
            <Info label={t("vvp_expires")} value={voucher.expires_at ? new Date(voucher.expires_at).toLocaleString() : "—"} />
          </div>
          {check && !check.ok && (
            <div className="rounded-lg border border-[oklch(0.78_0.2_50)]/40 bg-[oklch(0.78_0.2_50)]/10 p-2 text-[11px] text-[oklch(0.78_0.2_50)]">
              <AlertTriangle className="mr-1 inline h-3 w-3" />
              {t(`vvp_err_${check.reason}`) || check.reason}
            </div>
          )}
          <div className="flex gap-2">
            <button
              disabled={!check?.ok}
              onClick={onRedeem}
              className="flex-1 rounded-xl bg-[oklch(0.82_0.24_145)] px-4 py-2 text-sm font-bold text-background disabled:opacity-40"
            >
              <CheckCircle2 className="mr-1 inline h-4 w-4" /> {t("vvp_redeem")}
            </button>
            <button onClick={onReject} className="rounded-xl border border-border/60 px-4 py-2 text-sm font-bold">
              <XCircle className="mr-1 inline h-4 w-4" /> {t("vvp_reject")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const color = status === "active" ? "oklch(0.82 0.24 145)" : status === "redeemed" ? "oklch(0.7 0.05 250)" : "oklch(0.78 0.2 50)";
  return <span className="rounded px-2 py-0.5 text-[9px] font-bold uppercase" style={{ background: `color-mix(in oklab, ${color} 20%, transparent)`, color }}>{status}</span>;
}
function Info({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg bg-accent/30 p-2">
      <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={mono ? "font-mono text-xs" : "text-xs"}>{value}</div>
    </div>
  );
}
